/**
 * NoSubscription Component
 * SESSION_186: Frontend Billing Page
 *
 * Displayed when user has no active subscription.
 * Provides information about subscription benefits and guides user to claim a business.
 */

import React, { useCallback } from 'react';
import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  List,
  ThemeIcon,
  Group
} from '@mantine/core';
import {
  IconMessageCircle,
  IconBell,
  IconChartBar,
  IconCheck,
  IconBuilding
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { CUSTOMER_ROUTES } from '../../routing/routes';
import { useAuth } from '../../hooks/useAuth';

/**
 * NoSubscription Component
 *
 * Shows subscription benefits and guides user to claim a business
 * if they don't already have one.
 */
export const NoSubscription: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleClaimBusiness = useCallback((): void => {
    // Navigate to register page which handles business claiming
    navigate('/register');
  }, [navigate]);

  const handleViewBusiness = useCallback((): void => {
    navigate(CUSTOMER_ROUTES.MY_BUSINESS);
  }, [navigate]);

  const hasBusiness = !!user?.businessId;

  return (
    <Paper withBorder p="xl" radius="md">
      <Stack gap="lg" align="center">
        <ThemeIcon size={60} radius="xl" variant="light" color="blue">
          <IconMessageCircle size={32} />
        </ThemeIcon>

        <Stack gap="xs" align="center">
          <Title order={3} ta="center">
            No Active Subscription
          </Title>
          <Text c="dimmed" ta="center" maw={400}>
            Subscribe to MentionedMe to track what people are saying about your business
            online.
          </Text>
        </Stack>

        {/* Benefits List */}
        <Paper withBorder p="md" radius="md" w="100%" maw={400}>
          <Stack gap="md">
            <Text fw={600}>Subscription Benefits</Text>
            <List
              spacing="sm"
              size="sm"
              icon={
                <ThemeIcon color="green" size={20} radius="xl">
                  <IconCheck size={12} />
                </ThemeIcon>
              }
            >
              <List.Item
                icon={
                  <ThemeIcon color="blue" size={20} radius="xl">
                    <IconMessageCircle size={12} />
                  </ThemeIcon>
                }
              >
                Real-time mention tracking across online communities
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="orange" size={20} radius="xl">
                    <IconBell size={12} />
                  </ThemeIcon>
                }
              >
                Email notifications when your business is mentioned
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="grape" size={20} radius="xl">
                    <IconChartBar size={12} />
                  </ThemeIcon>
                }
              >
                Analytics dashboard with sentiment insights
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* Pricing */}
        <Group gap="xs">
          <Text size="xl" fw={700} c="blue">
            $25
          </Text>
          <Text c="dimmed">/ month</Text>
        </Group>

        {/* CTA */}
        {hasBusiness ? (
          <Stack gap="xs" align="center">
            <Text size="sm" c="dimmed" ta="center">
              Your business is set up but no active subscription was found.
              This may happen if payment setup was incomplete during registration.
            </Text>
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconBuilding size={16} />}
                onClick={handleViewBusiness}
              >
                View My Business
              </Button>
            </Group>
            <Text size="xs" c="dimmed" ta="center">
              If you completed payment during registration, please contact support.
            </Text>
          </Stack>
        ) : (
          <Stack gap="xs" align="center">
            <Button
              size="lg"
              leftSection={<IconBuilding size={20} />}
              onClick={handleClaimBusiness}
            >
              Claim Your Business
            </Button>
            <Text size="xs" c="dimmed">
              Start by claiming your business to set up tracking
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
